import { Show, For, createMemo, createSignal, type Component, type JSX } from "solid-js";
import { PanelLayout } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { usePanel } from "@/shared/use-panel";
import { useJobEvents } from "@/shared/use-job-events";
import { useWs } from "@/shared/ws";
import { registerPanel } from "@/panel-registry";
import { PanelChrome } from "../console/ui";
import { Skeleton } from "@/components/skeleton";
import {
  PanelBadge,
  PanelDetail,
  PanelKpi,
  PanelKpiGrid,
  RefreshIndicator,
  type DetailSection,
  type Intent,
} from "@/shared/panel-primitives";
import { usePanelRefresh } from "@/shared/use-panel-refresh";
import type { PluginJobEventPayload, WsIncoming } from "@/shared/types";
import { isWsEnvelope } from "@/shared/ws-protocol";
import "./services.css";

interface ServiceRow {
  name: string;
  displayName: string;
  status: string;
  startType: string;
  exePath?: string | null;
  pid?: number | null;
  accountName?: string | null;
  description?: string | null;
}

interface ServiceDetail extends ServiceRow {
  dependsOn: string[];
  dependents: string[];
}

interface ListResult {
  rows: ServiceRow[];
  count?: number;
}

type BulkAction = "start" | "stop" | "restart";

interface BulkProgress {
  action: BulkAction;
  total: number;
  done: number;
  failures: number;
}

const SYSTEM_CRITICAL = new Set(["TrustedInstaller", "WinDefend", "MpsSvc"]);

const BULK_LABELS: Record<BulkAction, string> = {
  start: "Démarrer la sélection",
  stop: "Arrêter la sélection",
  restart: "Redémarrer la sélection",
};

function statusIntent(status: string): Intent {
  if (status === "Running") return "ok";
  if (status === "Stopped") return "muted";
  return "warn";
}

function isAutoStart(startType: string): boolean {
  return /^auto/i.test(startType.trim());
}

const ServicesPanel: Component = () => {
  const ws = useWs();
  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const [filter, setFilter] = createSignal("");
  const [statusFilter, setStatusFilter] = createSignal("All");
  const [selected, setSelected] = createSignal<ServiceRow | null>(null);
  const [activeJobId, setActiveJobId] = createSignal<string | null>(null);
  const [pendingStartupType, setPendingStartupType] = createSignal<string | null>(null);
  const [checked, setChecked] = createSignal<Set<string>>(new Set());
  const [bulkProgress, setBulkProgress] = createSignal<BulkProgress | null>(null);

  const listQuery = createToolQuery<ListResult>(() => ({
    tool: "services_list",
    params: { status: statusFilter() === "All" ? undefined : statusFilter() },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const detailQuery = createToolQuery<ServiceDetail>(() => ({
    tool: "services_details",
    params: { name: selected()?.name ?? "" },
    enabled: enabled() && !!selected(),
    refetchInterval: 0,
  }));

  const panel = usePanel(
    () => ({ isFetching: listQuery.isFetching, error: listQuery.error }),
    () => ({ isFetching: detailQuery.isFetching, error: detailQuery.error }),
  );

  const refresh = usePanelRefresh({
    query: () => ({
      isFetching: listQuery.isFetching,
      refetch: () => listQuery.refetch(),
      dataUpdatedAt: listQuery.dataUpdatedAt,
      error: listQuery.error,
    }),
  });

  const jobState = useJobEvents(() => activeJobId());

  const allRows = createMemo<ServiceRow[]>(() => listQuery.data?.rows ?? []);

  const rows = createMemo(() => {
    const all = allRows();
    const q = filter().trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.displayName.toLowerCase().includes(q) ||
        (r.accountName ?? "").toLowerCase().includes(q),
    );
  });

  // ─── KPI cards — calculés depuis les données déjà chargées ─────────────
  const kpis = createMemo(() => {
    const all = allRows();
    let running = 0;
    let stopped = 0;
    let auto = 0;
    for (const r of all) {
      if (r.status === "Running") running += 1;
      else if (r.status === "Stopped") stopped += 1;
      if (isAutoStart(r.startType)) auto += 1;
    }
    return { total: all.length, running, stopped, auto };
  });

  // ─── Sélection multiple ────────────────────────────────────────────────
  const isChecked = (name: string) => checked().has(name);

  function toggleRow(name: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function toggleAll() {
    const visible = rows();
    setChecked((prev) => {
      const allSelected = visible.length > 0 && visible.every((r) => prev.has(r.name));
      if (allSelected) return new Set<string>();
      return new Set<string>(visible.map((r) => r.name));
    });
  }

  function clearSelection() {
    setChecked(new Set<string>());
  }

  const selectedRows = createMemo(() => rows().filter((r) => checked().has(r.name)));

  async function runServiceAction(name: string, action: BulkAction) {
    const tool = `services_${action}`;
    panel.setActionStatus({ text: `${action} en cours...`, level: "info" });
    try {
      const res = await panel.tools.call<{ jobId: string; async: boolean }>(
        tool,
        { name },
        { userConfirmedAt: new Date().toISOString() },
      );
      if (res?.jobId) {
        setActiveJobId(res.jobId);
        panel.setActionStatus({ text: `${action} lancé...`, level: "info" });
      }
    } catch (err) {
      panel.setActionStatus({ text: `Erreur: ${String(err)}`, level: "danger" });
    }
  }

  let prevJobDone = false;
  function checkJobDone(): boolean {
    const js = jobState();
    const done = js?.status === "done" || js?.status === "error";
    if (done && !prevJobDone) {
      prevJobDone = true;
      setTimeout(async () => {
        await listQuery.refetch();
        await detailQuery.refetch();
        setActiveJobId(null);
        panel.setActionStatus(null);
        prevJobDone = false;
      }, 800);
    } else if (!done) {
      prevJobDone = false;
    }
    return done;
  }

  // Attend la fin d'un job asynchrone via les events WS live/plugin/job.event.
  // Utilisé pour séquencer les actions groupées sans interférer avec
  // useJobEvents (qui suit le job du panneau de détail).
  function awaitJob(jobId: string, timeoutMs = 60_000): Promise<void> {
    return new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        off();
        clearTimeout(timer);
        resolve();
      };
      const off = ws.on(
        (m: WsIncoming): boolean =>
          isWsEnvelope(m, "live", "plugin", "job.event") &&
          (m as { payload?: { jobId?: string } }).payload?.jobId === jobId,
        (m: WsIncoming) => {
          const p = (m as { payload?: { event?: PluginJobEventPayload } }).payload?.event;
          if (!p) return;
          if (p.type === "done" || p.type === "error") finish();
        },
      );
      const timer = setTimeout(finish, timeoutMs);
    });
  }

  // ─── Action groupée ────────────────────────────────────────────────────
  async function runBulkAction(action: BulkAction) {
    const targets = selectedRows();
    if (targets.length === 0) return;

    // Confirmation pour les actions sensibles (stop/restart) ou si la
    // sélection touche un service système critique.
    const touchesCritical = targets.some((r) => SYSTEM_CRITICAL.has(r.name));
    if (action !== "start" || touchesCritical) {
      const ok = await panel.confirm.confirm({
        title: `${BULK_LABELS[action]}`,
        body:
          `${targets.length} service(s) concerné(s).` +
          (touchesCritical ? " Attention : la sélection contient un service système critique." : ""),
        level: touchesCritical ? "danger" : "warn",
        confirmLabel: "Confirmer",
      });
      if (!ok) return;
    }

    const confirmedAt = new Date().toISOString();
    const tool = `services_${action}`;
    setBulkProgress({ action, total: targets.length, done: 0, failures: 0 });
    panel.setActionStatus({ text: `${BULK_LABELS[action]}...`, level: "info" });

    for (const svc of targets) {
      try {
        const res = await panel.tools.call<{ jobId?: string; async?: boolean }>(
          tool,
          { name: svc.name },
          { userConfirmedAt: confirmedAt },
        );
        if (res?.jobId) await awaitJob(res.jobId);
        setBulkProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
      } catch {
        setBulkProgress((p) => (p ? { ...p, done: p.done + 1, failures: p.failures + 1 } : p));
      }
    }

    const finalState = bulkProgress();
    const failures = finalState?.failures ?? 0;
    panel.setActionStatus({
      text:
        failures > 0
          ? `${BULK_LABELS[action]} — ${failures} échec(s) sur ${targets.length}.`
          : `${BULK_LABELS[action]} — ${targets.length} service(s) traité(s).`,
      level: failures > 0 ? "danger" : "info",
    });
    await listQuery.refetch();
    await detailQuery.refetch();
    clearSelection();
    setTimeout(() => {
      setBulkProgress(null);
      panel.setActionStatus(null);
    }, 2500);
  }

  async function applyStartupType() {
    const st = pendingStartupType();
    const name = selected()?.name;
    if (!st || !name) return;
    const ok = await panel.confirm.confirm({
      title: `Changer démarrage — ${name}`,
      body: `Type de démarrage → ${st}`,
      level: "warn",
      confirmLabel: "Appliquer",
    });
    if (!ok) return;
    panel.setActionStatus({ text: "Application...", level: "info" });
    try {
      await panel.tools.call(
        "services_set_startup",
        { name, startupType: st },
        { userConfirmedAt: new Date().toISOString() },
      );
      panel.setActionStatus(null);
      setPendingStartupType(null);
      await detailQuery.refetch();
      await listQuery.refetch();
    } catch (err) {
      panel.setActionStatus({ text: `Erreur: ${String(err)}`, level: "danger" });
    }
  }

  const allVisibleChecked = createMemo(() => {
    const visible = rows();
    return visible.length > 0 && visible.every((r) => checked().has(r.name));
  });

  const columns: Column<ServiceRow>[] = [
    {
      id: "_select",
      header: () => (
        <input
          type="checkbox"
          class="svc-row-check"
          checked={allVisibleChecked()}
          title="Tout sélectionner"
          onChange={toggleAll}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      cell: (info) => {
        const r = info.row.original;
        return (
          <input
            type="checkbox"
            class="svc-row-check"
            checked={isChecked(r.name)}
            onChange={() => toggleRow(r.name)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      accessorKey: "status",
      header: "État",
      cell: (info) => (
        <PanelBadge intent={statusIntent(String(info.getValue() ?? ""))}>{String(info.getValue() ?? "")}</PanelBadge>
      ),
    },
    { accessorKey: "displayName", header: "Service" },
    {
      accessorKey: "name",
      header: "Nom système",
      cell: (info) => <code style={{ "font-size": "11px" }}>{String(info.getValue() ?? "")}</code>,
    },
    { accessorKey: "startType", header: "Démarrage" },
    { accessorKey: "accountName", header: "Compte" },
  ];

  const actions = (
    <>
      <input
        class="panel-filter-input"
        placeholder="Filtrer par nom..."
        value={filter()}
        onInput={(e) => setFilter(e.currentTarget.value)}
      />
      <select class="panel-select" value={statusFilter()} onChange={(e) => setStatusFilter(e.currentTarget.value)}>
        <option value="All">Tous</option>
        <option value="Running">En cours</option>
        <option value="Stopped">Arrêtés</option>
      </select>
      <button class="btn btn--xs btn--ghost" onClick={() => listQuery.refetch()}>
        ↺
      </button>
      <RefreshIndicator state={refresh} />
    </>
  );

  const detail = () => detailQuery.data ?? null;

  return (
    <PanelChrome icon="⚡" title="Services" tagType="core" actions={actions}>
      <PanelLayout title="Services Windows" status={panel.status()}>
        <Show
          when={listQuery.data || !listQuery.isFetching}
          fallback={
            <div style={{ padding: "8px" }}>
              <Skeleton variant="table" rows={10} />
            </div>
          }
        >
          {/* ─── KPI cards (PanelKpi — Lot 2 primitives) ────────────── */}
          <PanelKpiGrid>
            <PanelKpi label="Services" value={kpis().total} />
            <PanelKpi label="En cours" value={kpis().running} intent="ok" />
            <PanelKpi label="Arrêtés" value={kpis().stopped} intent="muted" />
            <PanelKpi label="Démarrage auto" value={kpis().auto} intent="info" />
          </PanelKpiGrid>

          {/* ─── Barre d'actions groupées ───────────────────────────── */}
          <Show when={selectedRows().length > 0}>
            <div class="svc-bulkbar">
              <span class="svc-bulkbar__count">{selectedRows().length} sélectionné(s)</span>
              <button class="btn btn--xs btn--ok" disabled={!!bulkProgress()} onClick={() => runBulkAction("start")}>
                ▶ {BULK_LABELS.start}
              </button>
              <button class="btn btn--xs btn--danger" disabled={!!bulkProgress()} onClick={() => runBulkAction("stop")}>
                ⏹ {BULK_LABELS.stop}
              </button>
              <button
                class="btn btn--xs btn--warn"
                disabled={!!bulkProgress()}
                onClick={() => runBulkAction("restart")}
              >
                ↺ {BULK_LABELS.restart}
              </button>
              <div class="svc-bulkbar__spacer" />
              <button class="btn btn--xs btn--ghost" disabled={!!bulkProgress()} onClick={clearSelection}>
                Effacer
              </button>
              <Show when={bulkProgress()}>
                {(bp) => (
                  <div class="svc-bulk-progress">
                    <div class="svc-bulk-progress__track">
                      <div
                        class="svc-bulk-progress__fill"
                        style={{ width: `${bp().total ? (bp().done / bp().total) * 100 : 0}%` }}
                      />
                    </div>
                    <span class="svc-bulk-progress__label">
                      {bp().done}/{bp().total} traité(s)
                      {bp().failures > 0 ? ` — ${bp().failures} échec(s)` : ""}
                    </span>
                  </div>
                )}
              </Show>
            </div>
          </Show>

          <div class="panel-split">
            <div class="panel-split-main">
              <DataTable<ServiceRow>
                rows={rows()}
                columns={columns}
                rowActions={(r) => (
                  <button
                    class="btn btn--xs btn--ghost"
                    onClick={() => {
                      setSelected(r);
                      setPendingStartupType(null);
                      setActiveJobId(null);
                    }}
                  >
                    Détails
                  </button>
                )}
              />
            </div>

            <Show when={selected()}>
              {(svc) => {
                const sections = (): DetailSection[] => {
                  const d = detail();
                  const out: DetailSection[] = [];

                  // ─── Identité ─────────────────────────────────────────
                  const identite: Array<[string, JSX.Element]> = [["Nom système", <code>{svc().name}</code>]];
                  if (d?.description) {
                    identite.push([
                      "Description",
                      <span style={{ "white-space": "normal", "font-family": "inherit" }}>{d.description}</span>,
                    ]);
                  }
                  identite.push([
                    "Type démarrage",
                    <>
                      <select
                        class="panel-select"
                        value={pendingStartupType() ?? d?.startType ?? svc().startType}
                        onChange={(e) => setPendingStartupType(e.currentTarget.value)}
                      >
                        <option value="Automatic">Automatique</option>
                        <option value="Manual">Manuel</option>
                        <option value="Disabled">Désactivé</option>
                      </select>
                      <Show when={pendingStartupType()}>
                        <button
                          class="btn btn--xs btn--primary"
                          style={{ "margin-left": "4px" }}
                          onClick={applyStartupType}
                        >
                          Appliquer
                        </button>
                      </Show>
                    </>,
                  ]);
                  out.push({ label: "Identité", items: identite });

                  // ─── Exécution ────────────────────────────────────────
                  const execItems: Array<[string, JSX.Element]> = [];
                  if (d?.exePath) execItems.push(["Chemin", <span title={d.exePath}>{d.exePath}</span>]);
                  if (d?.pid) execItems.push(["PID", <>{d.pid}</>]);
                  if (d?.accountName) execItems.push(["Compte", <>{d.accountName}</>]);
                  if (execItems.length > 0) out.push({ label: "Exécution", items: execItems });

                  // ─── Dépendances ──────────────────────────────────────
                  if (d?.dependsOn && d.dependsOn.length > 0) {
                    out.push({
                      label: "Dépend de",
                      children: (
                        <ul class="panel-detail-list">
                          <For each={d.dependsOn}>{(x) => <li>{x}</li>}</For>
                        </ul>
                      ),
                    });
                  }
                  if (d?.dependents && d.dependents.length > 0) {
                    out.push({
                      label: "Requis par",
                      children: (
                        <ul class="panel-detail-list">
                          <For each={d.dependents}>{(x) => <li>{x}</li>}</For>
                        </ul>
                      ),
                    });
                  }

                  // ─── Journal (job log) ────────────────────────────────
                  const js = jobState();
                  if (js) {
                    checkJobDone();
                    out.push({
                      label: "Journal",
                      children: (
                        <>
                          <PanelBadge intent={js.status === "done" ? "ok" : js.status === "error" ? "danger" : "info"}>
                            {js.status}
                          </PanelBadge>
                          <pre class="panel-job-log">
                            <For each={js.logs}>{(line) => <div class="panel-job-log-line">{line}</div>}</For>
                          </pre>
                        </>
                      ),
                    });
                  }

                  return out;
                };

                return (
                  <PanelDetail
                    title={
                      <>
                        {svc().displayName} <PanelBadge intent={statusIntent(svc().status)}>{svc().status}</PanelBadge>
                      </>
                    }
                    onClose={() => setSelected(null)}
                    actions={
                      <>
                        <button
                          class="btn btn--xs btn--ok"
                          disabled={svc().status === "Running"}
                          onClick={() => runServiceAction(svc().name, "start")}
                        >
                          ▶ Démarrer
                        </button>
                        <button
                          class="btn btn--xs btn--danger"
                          disabled={svc().status === "Stopped" || SYSTEM_CRITICAL.has(svc().name)}
                          onClick={() => runServiceAction(svc().name, "stop")}
                        >
                          ⏹ Arrêter
                        </button>
                        <button class="btn btn--xs btn--warn" onClick={() => runServiceAction(svc().name, "restart")}>
                          ↺ Redémarrer
                        </button>
                      </>
                    }
                    sections={sections()}
                  />
                );
              }}
            </Show>
          </div>
        </Show>
      </PanelLayout>
    </PanelChrome>
  );
};

registerPanel("services", ServicesPanel);
export default ServicesPanel;
